import { Autocomplete, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { ReactNode } from 'react';
import ConnectFormContent from '@/pages/Candidate/view/tabs/OneOffChecksTab/ConnectReport/FormContent';
import { formatBrowserDateTime } from '@/pages/Candidate/view/oneOffOrder/formatUtc';
import VendorField from '@/pages/Candidate/view/oneOffOrder/VendorField';
import ViewInVendor from '@/pages/Candidate/view/oneOffOrder/ViewInVendor';
import { OrderFactSource } from '@/pages/Candidate/view/oneOffOrder/orderFacts';
import { FCRAWorker, OneOffCheckRecord } from '@/types/turn-api';
import { OptionalMark, OrderFields, Pair, Recorded, RecordedLegend, SectionActions, Span2 } from './styled';

const LICENSE_PLACEHOLDER = "{current driver's license}";

const referenceHint = (vendor: string, turnId: string, licenseNumber: string) => {
  const name = vendor.trim().toLowerCase();
  if (name === 'connect') return `${turnId}-88@prod-turning-rest-api`;
  if (name === 'certn') return '{Certn order id}@prod-turning-rest-api';
  if (name === 'pendot' || name === 'pendott' || name === 'clearinghouse') {
    return licenseNumber.trim() || LICENSE_PLACEHOLDER;
  }
  return turnId;
};

const markedLabel = (text: string, mark: string) => (
  <>
    {text}
    <OptionalMark>{mark}</OptionalMark>
  </>
);

const AccountField = ({
  disabled,
  onChange,
  options,
  value,
}: {
  disabled: boolean;
  onChange: (value: string) => void;
  options: string[];
  value: string;
}) => {
  if (disabled) {
    return (
      <TextField
        disabled
        helperText="Stored for this report. It cannot be edited."
        label="Vendor Specific Account"
        size="small"
        value={value}
      />
    );
  }

  return (
    <Autocomplete
      options={options}
      onChange={(_, next) => onChange(next || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          helperText="Choose the Connect account for this order."
          label="Vendor Specific Account"
          size="small"
        />
      )}
      value={value || null}
    />
  );
};

const OrderSection = ({
  accountName,
  accountOptions,
  framed,
  leading,
  onAccountChange,
  onClose,
  onOrderIdChange,
  onOrderRecordedChange,
  onRecordChange,
  onReferenceIdChange,
  onVendorChange,
  save,
  orderId,
  orderLocked,
  orderRecorded,
  record,
  referenceId,
  requestedAt,
  requestedBy,
  vendor,
  viewSource,
  worker,
}: {
  accountName: string;
  accountOptions: string[];
  framed: boolean;
  leading?: ReactNode;
  onAccountChange: (value: string) => void;
  onClose: () => void;
  onOrderIdChange: (value: string) => void;
  onOrderRecordedChange: (value: boolean) => void;
  onRecordChange?: (record: OneOffCheckRecord.Base) => void;
  onReferenceIdChange: (value: string) => void;
  onVendorChange: (value: string) => void;
  orderId: string;
  orderLocked: boolean;
  orderRecorded: boolean;
  record: OneOffCheckRecord.Base | null;
  referenceId: string;
  requestedAt: string;
  requestedBy: string;
  save?: ReactNode;
  vendor: string;
  viewSource: OrderFactSource;
  worker?: FCRAWorker.Details;
}) => {
  const showAccount = vendor.trim().toLowerCase() === 'connect';
  const orderHelper = "The id for the vendor's system.";
  const referenceHelper = `Hint: ${referenceHint(vendor, worker?.turn_id || '', worker?.drivers_license_number || '')}`;
  const referencePortal = "The id you search with in the vendor's portal.";

  const recorded = (
    <Recorded>
      <FormControlLabel
        sx={{
          alignItems: 'center',
          height: '42px',
          marginLeft: 0,
          marginRight: 0,
          '& .MuiCheckbox-root': { padding: 0, marginRight: '8px' },
          '& .MuiFormControlLabel-label': { fontSize: '14px' },
        }}
        control={
          <Checkbox
            checked={orderRecorded}
            onChange={(event) => onOrderRecordedChange(event.target.checked)}
            inputProps={{ 'aria-label': 'Order requested' }}
          />
        }
        label="Order requested"
      />
      <RecordedLegend>Check this to mark that the check has been requested.</RecordedLegend>
    </Recorded>
  );

  const fields = (
    <>
      {framed && <legend>Order Information</legend>}
      {framed && <ViewInVendor align="end" source={viewSource} />}
      <Pair>
        {leading}
        <VendorField disabled={orderLocked} onChange={onVendorChange} value={vendor} />
        {showAccount ? (
          <AccountField
            disabled={orderLocked}
            onChange={onAccountChange}
            options={accountOptions}
            value={accountName}
          />
        ) : null}
        {orderLocked && record && worker ? (
          <Span2>
            <ConnectFormContent
              embedded
              onClose={onClose}
              onRecordChange={onRecordChange}
              record={record}
              recorded={recorded}
              worker={worker}
            />
          </Span2>
        ) : (
          <>
            <TextField
              helperText={orderHelper}
              label={framed ? markedLabel('Order ID', 'Optional') : 'Order ID'}
              onChange={(event) => onOrderIdChange(event.target.value)}
              size="small"
              value={orderId}
            />
            <TextField
              helperText={
                <>
                  <span style={{ display: 'block' }}>{referenceHelper}</span>
                  <span style={{ display: 'block' }}>{referencePortal}</span>
                </>
              }
              label={markedLabel('Reference ID', 'Required')}
              onChange={(event) => onReferenceIdChange(event.target.value)}
              size="small"
              value={referenceId}
            />
            {recorded}
          </>
        )}
        {(requestedBy || requestedAt) && (
          <Span2>
            <Pair>
              {requestedBy ? <TextField disabled label="Requested by" size="small" value={requestedBy} /> : null}
              {requestedAt ? (
                <TextField disabled label="Requested at" size="small" value={formatBrowserDateTime(requestedAt)} />
              ) : null}
            </Pair>
          </Span2>
        )}
      </Pair>
      {save ? <SectionActions>{save}</SectionActions> : null}
    </>
  );

  if (!framed) return fields;

  return <OrderFields>{fields}</OrderFields>;
};

export default OrderSection;
